import React, { useState, useEffect } from 'react';
import { CloseIcon, UploadCloudIcon } from '../../../public/icons';
import { useToast } from '../../../context/ToastContext';
import adminMemoirService from '../../../services/admin/adminMemoirService';

const AddCategoryModal = ({ isOpen, onClose, onCategoryAdded, categoryToEdit }) => {
  const { success, error: showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    coverImage: null,
    coverImagePublicId: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [existingCoverImage, setExistingCoverImage] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (categoryToEdit) {
        setFormData({
          name: categoryToEdit.name || '',
          description: categoryToEdit.description || '',
          coverImage: categoryToEdit.coverImage || null,
          coverImagePublicId: categoryToEdit.coverImagePublicId || null
        });
        setExistingCoverImage(categoryToEdit.coverImage);
        setPreviewImage(categoryToEdit.coverImage);
      } else {
        setFormData({
          name: '',
          description: '',
          coverImage: null,
          coverImagePublicId: null
        });
        setPreviewImage(null);
        setExistingCoverImage(null);
      }
      setIsSubmitting(false);
      setUploadingImage(false);
    }
  }, [isOpen, categoryToEdit]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError('Image size should be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('Only image files are allowed');
      return;
    }

    setUploadingImage(true);
    
    const preview = URL.createObjectURL(file);
    setPreviewImage(preview);
    
    try {
      const uploadResult = await adminMemoirService.uploadImageToCloudinary(file);
      
      setFormData(prev => ({
        ...prev,
        coverImage: uploadResult.url,
        coverImagePublicId: uploadResult.publicId
      }));
      setExistingCoverImage(null);
    } catch (error) {
      console.error('Error uploading image:', error);
      showError('Failed to upload image. Please try again.');
      setPreviewImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    if (previewImage && !existingCoverImage) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
    setFormData(prev => ({
      ...prev,
      coverImage: null,
      coverImagePublicId: null
    }));
    setExistingCoverImage(null);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      if (!formData.name || !formData.description) {
        showError('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }
      
      if (!formData.coverImage && !existingCoverImage) {
        showError('Please upload a cover image');
        setIsSubmitting(false);
        return;
      }
      
      let response;
      if (categoryToEdit) {
        response = await adminMemoirService.updateCategory(categoryToEdit._id, {
          name: formData.name,
          description: formData.description,
          coverImage: formData.coverImage || existingCoverImage,
          coverImagePublicId: formData.coverImagePublicId
        });
        success(response.message || 'Category updated successfully');
      } else {
        response = await adminMemoirService.createCategory({
          name: formData.name,
          description: formData.description,
          coverImage: formData.coverImage,
          coverImagePublicId: formData.coverImagePublicId
        });
        success(response.message || 'Category added successfully');
      }
      
      if (onCategoryAdded) {
        onCategoryAdded(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting category:', error);
      showError(error.response?.data?.message || 'Failed to save category. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitEnabled = formData.name && formData.description && (formData.coverImage || existingCoverImage) && !isSubmitting && !uploadingImage;
  const isEditMode = !!categoryToEdit;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300 text-left"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#FFFFFF] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out shadow-xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b px-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">{isEditMode ? 'Edit Category' : 'Add New Category'}</h2>
              <p className="text-[#6B635D] text-sm font-light mt-1">
                {isEditMode ? 'Update category information' : 'Organize questions by theme or purpose'}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded-sm transition-colors"
              disabled={isSubmitting || uploadingImage}
            >
              <CloseIcon className="w-5 h-5 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 text-left">
            {/* Category Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Category Name <span className="text-[#C03A2B]">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g, family roots, success trajectory, heartbreak & depression"
                className="w-full p-3 border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                disabled={isSubmitting || uploadingImage}
              />
            </div>

            {/* Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Description <span className="text-[#C03A2B]">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Brief description of memoir category..."
                className="w-full p-3 border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none"
                disabled={isSubmitting || uploadingImage}
              />
            </div>

            {/* Cover Image */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Cover Image <span className="text-[#C03A2B]">*</span>
              </label>
              
              {!previewImage ? (
                <div className={`border-2 border-dashed border-[#DBDBDB] p-8 text-center hover:border-[#D2A146] transition-colors cursor-pointer ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="coverImage"
                    disabled={isSubmitting || uploadingImage}
                  />
                  <label htmlFor="coverImage" className={`cursor-pointer ${uploadingImage ? 'cursor-not-allowed' : ''}`}>
                    <div className="flex justify-center items-center mb-4">
                      {uploadingImage ? (
                        <div className="w-12 h-12 border-4 border-[#D2A146] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <UploadCloudIcon className="w-12 h-12 text-gray-400" />
                      )}
                    </div>
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      {uploadingImage ? 'Uploading image...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-sm text-gray-500">PNG, JPG up to 5mb</p>
                  </label>
                </div>
              ) : (
                <div className="relative group">
                  <img
                    src={previewImage}
                    alt="Category cover"
                    className="w-full h-48 object-cover border border-gray-200 rounded-sm"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-600 transition-colors shadow-md"
                    disabled={isSubmitting || uploadingImage}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#DBDBDB]">
            <button 
              onClick={handleSubmit}
              disabled={!isSubmitEnabled}
              className={`w-full px-6 py-3 font-medium rounded-sm transition-colors flex items-center justify-center gap-2 ${
                isSubmitEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {(isSubmitting || uploadingImage) && (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Category' : 'Add Category')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCategoryModal;